import { Modal } from '@/components/ui/daisy/modal';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import ScheduledLesson from '@/models/ScheduledLesson';
import ScheduledLessonReportModalContent from './ScheduledLessonReportModalContent';
import useScheduledLessonReportData from '@/data/hook/scheduled-lesson-report/useScheduledLessonReportData';
import { NoPublishedScheduledLessonView } from './NoPublishedScheduledLessonView';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { getErrorMessage } from '@/utils/getErrorMessage';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import { Skeleton } from '@/components/ui/daisy';
import ScheduledLessonReportPermission from '@/utils/permissions/ScheduledLessonReportPermission';
import useAuth from '@/data/hook/useAuth';

type ScheduledLessonReportModalProps = {
  scheduledLessonId: number;
  onClose: () => void;
  cacheManager?: ListCacheManager<ScheduledLesson>;
};

export default function ScheduledLessonReportModal({
  scheduledLessonId,
  onClose,
  cacheManager,
}: ScheduledLessonReportModalProps) {
  const { ref, close } = useModalDialog(true, onClose);
  const { klass, scheduledLesson, error, loading } =
    useScheduledLessonReportData(scheduledLessonId);

  const { user } = useAuth();

  const permission = new ScheduledLessonReportPermission({
    user,
    model: scheduledLesson,
    klass,
  });

  const isLessonPublished = permission.canEditWhenPublished();

  const errorDetail = getErrorMessage(error);

  return (
    <Modal ref={ref} className="modal-bottom sm:modal-middle">
      <Modal.Box
        className={twMerge(
          'max-w-[950px] w-9/12 aspect-video flex flex-col gap-8',
          isLessonPublished ? 'min-h-[90vh]' : 'min-h-[40vh]',
        )}
      >
        <ConditionalRenderer condition={loading}>
          <Loading />
        </ConditionalRenderer>
        <ConditionalRenderer condition={error}>
          <ErrorComponent errorTextTitle={errorDetail} />
        </ConditionalRenderer>
        <ConditionalRenderer
          condition={isLessonPublished}
          fallback={
            <NoPublishedScheduledLessonView
              klassId={(klass && klass.id) || 0}
            />
          }
        >
          <ScheduledLessonReportModalContent
            klass={klass}
            scheduledLesson={scheduledLesson}
            onClose={close}
            cacheManager={cacheManager}
          />
        </ConditionalRenderer>
        <Modal.Close />
      </Modal.Box>
      <Modal.Backdrop />
    </Modal>
  );
}

function Loading() {
  return (
    <div className="flex flex-col flex-1 gap-8">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-6 w-28 rounded-full" />
      </div>

      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-80 w-full" />
      </div>

      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-40 w-full" />
      </div>

      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-30" />
      </div>

      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>

      <div className="flex gap-3 items-end h-full">
        <Skeleton className="h-12 flex-1 rounded-lg" />
        <Skeleton className="h-12 flex-1 rounded-lg" />
      </div>
    </div>
  );
}
